import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { FileText, Target, Sparkles, BookOpen, LogOut, Menu, ChevronRight, Download, Smartphone } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import logo from "@/assets/logo.jpeg";

const Navbar = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { isInstallable, isInstalled, isStandalone, promptInstall, isIOS } = usePWAInstall();
  const [isOpen, setIsOpen] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    setIsOpen(false);
    navigate("/");
  };

  const handleNavigation = (path: string) => {
    setIsOpen(false);
    navigate(path);
  };

  const handleInstallClick = async () => {
    if (isInstallable) {
      await promptInstall();
    } else {
      navigate("/install");
    }
  };
  const MobileMenuItem = ({
    icon: Icon,
    title,
    description,
    path
  }: {
    icon: React.ElementType;
    title: string;
    description: string;
    path: string;
  }) => <button onClick={() => handleNavigation(path)} className="flex items-center gap-4 p-4 rounded-xl hover:bg-muted/80 transition-all duration-200 w-full text-left active:scale-[0.98] touch-manipulation">
      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-foreground">{title}</div>
        <p className="text-sm text-muted-foreground truncate">{description}</p>
      </div>
      <ChevronRight className="w-4 h-4 text-muted-foreground" />
    </button>;
  return <header className="fixed top-0 left-0 right-0 z-50 bg-background/90 backdrop-blur-md border-b border-border/50">
      <div className="container mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/" onClick={() => setIsOpen(false)} className="flex items-center gap-2 z-10">
          <div className="bg-white rounded-lg p-1.5 shadow-sm">
            <img src={logo} alt="AI CV WALA" className="h-10 sm:h-12 w-auto" width="45" height="40" />
          </div>
          <span className="hidden sm:block font-bold text-lg text-foreground">AI CV WALA</span>
        </Link>
        
        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-6">
          <NavigationMenu>
            <NavigationMenuList>
              <NavigationMenuItem>
                <NavigationMenuTrigger className="bg-transparent">Features</NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid gap-3 p-4 w-[400px]">
                    <li>
                      <NavigationMenuLink asChild>
                        <Link to="/dashboard" className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors">
                          <FileText className="w-5 h-5 text-primary" />
                          <div>
                            <div className="font-medium">Resume Builder</div>
                            <p className="text-sm text-muted-foreground">Create professional resumes with AI</p>
                          </div>
                        </Link>
                      </NavigationMenuLink>
                    </li>
                    <li>
                      <NavigationMenuLink asChild>
                        <Link to="/dashboard" className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors">
                          <Target className="w-5 h-5 text-primary" />
                          <div>
                            <div className="font-medium">ATS Optimizer</div>
                            <p className="text-sm text-muted-foreground">Check your ATS score instantly</p>
                          </div>
                        </Link>
                      </NavigationMenuLink>
                    </li>
                    <li>
                      <NavigationMenuLink asChild>
                        <Link to="/dashboard" className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors">
                          <Sparkles className="w-5 h-5 text-primary" />
                          <div>
                            <div className="font-medium">AI Writing Assistant</div>
                            <p className="text-sm text-muted-foreground">Polish and rewrite with AI</p>
                          </div>
                        </Link>
                      </NavigationMenuLink>
                    </li>
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>
              
              <NavigationMenuItem>
                <NavigationMenuTrigger className="bg-transparent">Resources</NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid gap-3 p-4 w-[400px]">
                    <li>
                      <NavigationMenuLink asChild>
                        <Link to="/blog" className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors">
                          <BookOpen className="w-5 h-5 text-primary" />
                          <div>
                            <div className="font-medium">Blog</div>
                            <p className="text-sm text-muted-foreground">Resume tips and career guides</p>
                          </div>
                        </Link>
                      </NavigationMenuLink>
                    </li>
                    <li>
                      <NavigationMenuLink asChild>
                        <Link to="/blog/ats-friendly-resume-format" className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors">
                          <Target className="w-5 h-5 text-primary" />
                          <div>
                            <div className="font-medium">ATS Format Guide</div>
                            <p className="text-sm text-muted-foreground">Beat applicant tracking systems</p>
                          </div>
                        </Link>
                      </NavigationMenuLink>
                    </li>
                    <li>
                      <NavigationMenuLink asChild>
                        <Link to="/blog/resume-examples-by-industry" className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors">
                          <FileText className="w-5 h-5 text-primary" />
                          <div>
                            <div className="font-medium">Resume Examples</div>
                            <p className="text-sm text-muted-foreground">50+ industry-specific samples</p>
                          </div>
                        </Link>
                      </NavigationMenuLink>
                    </li>
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>
          
          <Link to="/pricing" className="text-sm font-medium text-foreground hover:text-primary transition-colors">
            Pricing
          </Link>
          <Link to="/blog" className="text-sm font-medium text-foreground hover:text-primary transition-colors">
            Blog
          </Link>
        </nav>
        
        {/* Desktop Auth Buttons */}
        <div className="hidden lg:flex items-center gap-3">
          {/* Install App Button - show if installable or on iOS */}
          {!isInstalled && !isStandalone && (
            <Button
              variant="outline"
              size="sm"
              className="gap-2 border-primary/30 text-primary hover:bg-primary/10"
              onClick={handleInstallClick}
            >
              <Download className="w-4 h-4" />
              Install App
            </Button>
          )}
          
          <ThemeToggle />
          {user ? <>
              <Button className="bg-primary hover:bg-primary/90 text-primary-foreground" onClick={() => navigate('/dashboard')}>
                Dashboard
              </Button>
              <Button variant="ghost" className="text-foreground hover:text-primary gap-2" onClick={handleSignOut}>
                <LogOut className="w-4 h-4" />
                Sign Out
              </Button>
            </> : <>
              <Button variant="ghost" className="text-foreground hover:text-primary" onClick={() => navigate('/auth')}>
                Login
              </Button>
              <Button className="bg-primary hover:bg-primary/90 text-primary-foreground" onClick={() => navigate('/auth')}>
                Get Started
              </Button>
            </>}
        </div>

        {/* Mobile Menu */}
        <div className="lg:hidden flex items-center gap-2">
          <ThemeToggle />
          {user && <Button size="sm" className="bg-primary hover:bg-primary/90 text-primary-foreground" onClick={() => navigate('/dashboard')}>
              Dashboard
            </Button>}
          
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="touch-manipulation" aria-label="Open menu">
                <Menu className="w-6 h-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:w-[400px] p-0 overflow-y-auto">
              <SheetHeader className="p-6 border-b border-border/50">
                <SheetTitle className="text-left flex items-center gap-2">
                  <div className="bg-white rounded-lg p-1.5 shadow-sm">
                    <img src={logo} alt="AI CV WALA" className="h-8 w-auto" width="36" height="32" />
                  </div>
                  <span className="font-bold text-lg">AI CV WALA</span>
                </SheetTitle>
              </SheetHeader>
              
              <div className="p-4 space-y-6">
                {/* Features Section */}
                <div>
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-4 mb-2">
                    Features
                  </h3>
                  <div className="space-y-1">
                    <MobileMenuItem icon={FileText} title="Resume Builder" description="Create professional resumes with AI" path="/dashboard" />
                    <MobileMenuItem icon={Target} title="ATS Optimizer" description="Check your ATS score instantly" path="/dashboard" />
                    <MobileMenuItem icon={Sparkles} title="AI Writing Assistant" description="Polish and rewrite with AI" path="/dashboard" />
                  </div>
                </div>

                {/* Resources Section */}
                <div>
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-4 mb-2">
                    Resources
                  </h3>
                  <div className="space-y-1">
                    <MobileMenuItem icon={BookOpen} title="Blog" description="Resume tips and career guides" path="/blog" />
                    <MobileMenuItem icon={Target} title="ATS Format Guide" description="Beat applicant tracking systems" path="/blog/ats-friendly-resume-format" />
                    <MobileMenuItem icon={FileText} title="Resume Examples" description="50+ industry-specific samples" path="/blog/resume-examples-by-industry" />
                  </div>
                </div>

                {/* Quick Links */}
                <div className="space-y-1">
                  <button onClick={() => handleNavigation('/pricing')} className="flex items-center gap-4 p-4 rounded-xl hover:bg-muted/80 transition-all w-full text-left touch-manipulation">
                    <span className="font-semibold text-foreground">Pricing</span>
                  </button>
                </div>

                {/* Install App - Mobile */}
                {!isInstalled && !isStandalone && (
                  <div>
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-4 mb-2">
                      Get the App
                    </h3>
                    <button
                      onClick={() => handleNavigation('/install')}
                      className="flex items-center gap-4 p-4 rounded-xl bg-primary/5 border border-primary/20 hover:bg-primary/10 transition-all w-full text-left touch-manipulation"
                    >
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <Smartphone className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-foreground">Install AI CV WALA</div>
                        <p className="text-sm text-muted-foreground">Quick access & offline mode</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-primary" />
                    </button>
                  </div>
                )}

                {/* Auth Section */}
                <div className="pt-4 border-t border-border/50 space-y-3">
                  {user ? <Button variant="outline" className="w-full justify-start gap-3 h-12 touch-manipulation" onClick={handleSignOut}>
                      <LogOut className="w-5 h-5" />
                      Sign Out
                    </Button> : <>
                      <Button className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground touch-manipulation" onClick={() => handleNavigation('/auth')}>
                        Get Started Free
                      </Button>
                      <Button variant="outline" className="w-full h-12 touch-manipulation" onClick={() => handleNavigation('/auth')}>
                        Login
                      </Button>
                    </>}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>;
};
export default Navbar;